/* Inquiry into Creation（#106）。スクロール進捗と文字の出現をセクション単位で管理する。 */
(() => {
  const SECTION_SELECTOR = '[data-section-type="stilllife-inquiry"]';
  const TEXT_REVEAL_TARGET_SELECTOR = '[data-text-reveal]';
  const PANEL_SELECTOR = '[data-concept-panel]';
  // エディタでスクリプト自体が再評価された場合も、前回の登録を解除する。
  // name="slInquiryCleanup" の要素があると window.slInquiryCleanup がその要素になるため（DOM clobbering）、
  // 関数かどうかまで確かめてから呼ぶ。truthy だけで判定すると TypeError で以降の初期化が全部止まる（#106 レビュー H）。
  if (typeof window.slInquiryCleanup === 'function') window.slInquiryCleanup();
  const instances = new Map();

  // CSSのゲート。パネル2〜5とコピーを隠してよいのは、このスクリプトが実際に動いたときだけ。
  // html.js（JSが有効か）を条件にすると、アセット404・CSP・拡張のブロックでスクリプトが
  // 読み込まれなかった場合も隠れたままになる。ここで自分から付け、初期化が例外で落ちたら外す。
  // これで CSS 側は html:not(.sl-inquiry-ready) の縦積みフォールバックへ落ちる（#106 レビュー M2）。
  const READY_CLASS = 'sl-inquiry-ready';
  const root = document.documentElement;
  root.classList.add(READY_CLASS);
  const fallBackToStatic = () => root.classList.remove(READY_CLASS);

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const parseHexColor = (value) => {
    const hex = value.trim().replace('#', '');
    if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(hex)) return null;

    const normalized = hex.length === 3
      ? hex.split('').map((char) => char + char).join('')
      : hex;

    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16)
    };
  };

  const mixColor = (from, to, ratio) => ({
    r: Math.round(from.r + (to.r - from.r) * ratio),
    g: Math.round(from.g + (to.g - from.g) * ratio),
    b: Math.round(from.b + (to.b - from.b) * ratio)
  });

  const easeInOutSine = (value) => {
    const progress = clamp(value, 0, 1);
    return -(Math.cos(Math.PI * progress) - 1) / 2;
  };

  const toRgbString = ({ r, g, b }) => `rgb(${r} ${g} ${b})`;

  // innerHTML を経由せず、既にあるDOMを辿って行に分ける。
  // <br> を改行、それ以外の要素は中身を連結して扱う（#106 レビュー I）。
  const extractLinesFromElement = (element) => {
    const lines = [];
    let current = '';

    const walk = (node) => {
      node.childNodes.forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) {
          current += child.nodeValue;
          return;
        }
        if (child.nodeType !== Node.ELEMENT_NODE) return;
        if (child.tagName === 'BR') {
          lines.push(current);
          current = '';
          return;
        }
        walk(child);
      });
    };

    walk(element);
    lines.push(current);

    return lines
      .map((line) => line.replace(/ /g, ' ').trim())
      .filter((line) => line.length > 0);
  };

  const createRevealUnit = (text, delayMs) => {
    const unit = document.createElement('span');
    unit.className = 'js-reveal-text-unit';
    unit.style.setProperty('--reveal-delay', `${delayMs}ms`);
    unit.textContent = text;
    return unit;
  };

  const prepareTextRevealTarget = (target) => {
    if (target.classList.contains('js-reveal-text')) return true;

    const mode = (target.getAttribute('data-text-reveal') || '').trim();
    if (mode !== 'word' && mode !== 'line') return false;

    const lines = extractLinesFromElement(target);
    if (!lines.length) return false;

    // <p> の暗黙ロール paragraph は aria-label を許さないため付けない。
    // 分割後も span のテキストがそのまま残るので、読み上げの内容は変わらない（#106 レビュー J）。

    const fragment = document.createDocumentFragment();
    const unitCount = mode === 'line'
      ? lines.length
      : lines.reduce((count, line) => count + line.split(/\s+/).filter((word) => word.length > 0).length, 0);

    const baseDelayStep = mode === 'word' ? 62 : 170;
    const minSpan = mode === 'word' ? 860 : 760;
    const maxDelayStep = mode === 'word' ? 240 : 320;
    const startDelay = mode === 'word' ? 120 : 160;

    const dynamicDelayStep = unitCount > 1
      ? clamp(Math.round(minSpan / (unitCount - 1)), baseDelayStep, maxDelayStep)
      : 0;

    let unitIndex = 0;

    lines.forEach((line, lineIndex) => {
      if (mode === 'line') {
        fragment.appendChild(createRevealUnit(line, startDelay + unitIndex * dynamicDelayStep));
        unitIndex += 1;
      } else {
        const words = line.split(/\s+/).filter((word) => word.length > 0);
        words.forEach((word, wordIndex) => {
          fragment.appendChild(createRevealUnit(word, startDelay + unitIndex * dynamicDelayStep));
          unitIndex += 1;
          if (wordIndex < words.length - 1) {
            fragment.appendChild(document.createTextNode(' '));
          }
        });
      }

      if (lineIndex < lines.length - 1) {
        fragment.appendChild(document.createElement('br'));
      }
    });

    target.textContent = '';
    target.appendChild(fragment);
    target.classList.add('js-reveal-text');
    return true;
  };

  const setupTextReveal = (section, motion) => {
    const targets = Array.from(section.querySelectorAll(TEXT_REVEAL_TARGET_SELECTOR))
      .filter((target) => target instanceof HTMLElement)
      .filter((target) => prepareTextRevealTarget(target));

    // パネル内のコピーは5枚が同じ位置に重なっており、初回に5要素すべてが交差する。
    // IntersectionObserver では「アクティブなパネルの文字が出る」演出にならないので、
    // 出現の制御はパネルに付く is-text-visible に任せる（#106 レビュー S）。
    const panelTargets = targets.filter((target) => target.closest(PANEL_SELECTOR));

    // パネルの外に [data-text-reveal] を置いた場合は制御する主体がいないので、その場で出す。
    // 現状の markup では該当0件だが、隠したまま戻せなくなる経路を残さない（#106 レビュー S7）。
    targets.filter((target) => !target.closest(PANEL_SELECTOR))
      .forEach((target) => target.classList.add('is-visible'));

    // モーションを減らす設定では縦積みになり全パネルが同時に見えるので、まとめて出す。
    // 通常の設定へ戻ったときは is-text-visible の制御へ返す。
    const applyMotionState = () => {
      panelTargets.forEach((target) => target.classList.toggle('is-visible', motion.matches));
    };

    applyMotionState();
    motion.addEventListener('change', applyMotionState);
    return () => {
      motion.removeEventListener('change', applyMotionState);
    };
  };

  const setupConceptSequence = (section) => {
    const sequence = section.querySelector('[data-concept-sequence]');
    if (!(sequence instanceof HTMLElement)) return;

    const panels = Array.from(sequence.querySelectorAll(PANEL_SELECTOR));
    if (!panels.length) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const cleanupText = setupTextReveal(section, motion);
    // 地色はこのページだけ body スコープで暗く上書きしてある（stilllife-inquiry.css・レビュー E）。
    // そのため読み取りも書き込みも body で行う。:root へ書くと body 側の --bg-dynamic-* 宣言に負けて効かない。
    const backgroundHost = document.body;
    const backgroundProperties = ['--bg-dynamic-start', '--bg-dynamic-mid', '--bg-dynamic-end'];
    const previousBackground = backgroundProperties.map((property) => backgroundHost.style.getPropertyValue(property));
    const styles = getComputedStyle(backgroundHost);
    const base1 = parseHexColor(styles.getPropertyValue('--bg-base-1'));
    const base2 = parseHexColor(styles.getPropertyValue('--bg-base-2'));
    const base3 = parseHexColor(styles.getPropertyValue('--bg-base-3'));
    const palette = base1 && base2 && base3 ? [base2, base1, base3, base2, base1] : [];

    const resolvePaletteColor = (progress) => {
      const transitionDuration = 0.12;
      const normalizedProgress = clamp(progress, 0, 1);
      let currentColor = palette[0];

      for (let index = 1; index < palette.length; index += 1) {
        const transitionStart = (index - 0.5) / (palette.length - 1);
        const transitionEnd = transitionStart + transitionDuration;

        if (normalizedProgress < transitionStart) return currentColor;
        if (normalizedProgress <= transitionEnd) {
          const colorProgress = (normalizedProgress - transitionStart) / transitionDuration;
          return mixColor(currentColor, palette[index], easeInOutSine(colorProgress));
        }

        currentColor = palette[index];
      }

      return currentColor;
    };

    let lastAppliedColorKey = '';
    const applyBackground = (progress) => {
      if (!palette.length) return;
      // モーションを減らす設定では地色を1色に固定する。補間だけをやめると全画面の地色が
      // スクロールで5色ハードカットし、前庭障害・光過敏には補間よりも負担が大きい（#106 レビュー S5）。
      // 固定色は palette[0]（= --bg-base-2）で、CSSの background-color: var(--bg-dynamic-end) と同じ。
      const color = toRgbString(motion.matches ? palette[0] : resolvePaletteColor(progress));
      if (color === lastAppliedColorKey) return;
      lastAppliedColorKey = color;

      backgroundHost.style.setProperty('--bg-dynamic-start', color);
      backgroundHost.style.setProperty('--bg-dynamic-mid', color);
      backgroundHost.style.setProperty('--bg-dynamic-end', color);
    };

    let activeIndex = -1;
    let rafId = 0;

    const setActive = (index) => {
      const nextIndex = clamp(index, 0, panels.length - 1);
      if (nextIndex === activeIndex && panels[nextIndex].classList.contains('is-active')) return;
      activeIndex = nextIndex;
      panels.forEach((panel, panelIndex) => {
        const isActive = panelIndex === activeIndex;
        panel.classList.toggle('is-active', isActive);
        panel.classList.toggle('is-text-visible', isActive);
        /* 前後1枚は content-visibility を戻して画像だけ先に落とす（#124）。
           visibility: hidden のままなので画面にも検索にも出ない。
           消えていく側も隣に入るため、フェードアウトが描画される（#123） */
        panel.classList.toggle('is-near', Math.abs(panelIndex - activeIndex) === 1);
      });
    };

    const updateFromScroll = () => {
      rafId = 0;
      const sequenceTop = sequence.getBoundingClientRect().top + window.scrollY;
      const scrollRange = Math.max(1, sequence.offsetHeight - window.innerHeight);
      const progress = clamp((window.scrollY - sequenceTop) / scrollRange, 0, 1);
      applyBackground(progress);
      setActive(Math.round(progress * (panels.length - 1)));
    };

    const requestUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateFromScroll);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    motion.addEventListener('change', requestUpdate);
    requestUpdate();

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      motion.removeEventListener('change', requestUpdate);
      window.cancelAnimationFrame(rafId);
      cleanupText();
      backgroundProperties.forEach((property, index) => {
        if (previousBackground[index]) backgroundHost.style.setProperty(property, previousBackground[index]);
        else backgroundHost.style.removeProperty(property);
      });
    };
  };

  const initialize = (scope) => {
    // 古いSafariに MediaQueryList.addEventListener が無いなど、初期化の途中で例外が出ることがある。
    // 握りつぶさずにフォールバックへ落とし、読めない状態のまま残さない（#106 レビュー M2）。
    try {
      const sections = scope instanceof Element && scope.matches(SECTION_SELECTOR)
        ? [scope] : Array.from(scope.querySelectorAll(SECTION_SELECTOR));
      sections.forEach((section) => {
        if (instances.has(section)) instances.get(section)();
        const cleanup = setupConceptSequence(section);
        if (cleanup) instances.set(section, cleanup);
      });
    } catch (error) {
      fallBackToStatic();
      console.error('[stilllife-inquiry] 初期化に失敗したため縦積み表示へ切り替えました', error);
    }
  };

  const onLoad = (event) => initialize(event.target);
  const onUnload = (event) => {
    instances.forEach((cleanup, section) => {
      if (event.target === section || event.target.contains(section)) {
        cleanup();
        instances.delete(section);
      }
    });
  };
  const onReady = () => initialize(document);

  document.addEventListener('shopify:section:load', onLoad);
  document.addEventListener('shopify:section:unload', onUnload);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onReady, { once: true });
  } else {
    onReady();
  }

  window.slInquiryCleanup = () => {
    document.removeEventListener('DOMContentLoaded', onReady);
    document.removeEventListener('shopify:section:load', onLoad);
    document.removeEventListener('shopify:section:unload', onUnload);
    instances.forEach((cleanup) => cleanup());
    instances.clear();
    // 再評価されたスクリプトが自分で付け直すので、ここでは必ず外して素の状態へ戻す。
    fallBackToStatic();
  };
})();
